import { Link } from 'react-router-dom'
import { Chip } from '../../components/ui/Chip'
import { EmptyState } from '../../components/ui/EmptyState'
import { Skeleton } from '../../components/ui/Skeleton'
import { PartThumbnails } from './PartThumbnails'
import type { OrderSummary } from './domain'
import {
  ORDER_STATUS_CHIP_META,
  formatEstDelivery,
  formatMoney,
  formatPartsSummary,
  resolveOrderIdentity,
  resolvePartNamesDisplay,
} from './domain'

export type OrderTableSortColumn = 'placedAt' | 'total'
export type SortDirection = 'ascending' | 'descending'

export interface OrderTableProps {
  readonly orders: readonly OrderSummary[]
  readonly sortColumn: OrderTableSortColumn
  readonly sortDirection: SortDirection
  readonly onSort: (column: OrderTableSortColumn) => void
  readonly hasAnySearchOrFilterActive: boolean
  readonly onClearFilters: () => void
}

/**
 * The order-history table (UX spec §1.2 item 5, components.md "Table/data
 * row"). Real `<table>`/`<thead>`/`<tbody>`/`<th scope="col">` — not styled
 * divs — so assistive tech gets row/column announcement (§1.9). Sortable
 * headers expose `aria-sort` and are buttons within the `<th>`. Mobile
 * (<768px) collapses to a stacked card list via CSS only (`.order-table` /
 * `.order-row-card` share the same markup, `orders.css` switches the visual
 * presentation at the breakpoint) rather than a second component, so there
 * is exactly one source of row markup to keep in sync.
 *
 * Exactly 5 columns, matching the standalone `/orders` prototype binding
 * (`ordersRows`, `Fictiv Platform.dc.html` ~L3629-3651) verbatim: ORDER
 * (SO ref primary / order name secondary) · PARTS (thumbnails + count +
 * names) · STATUS · EST. DELIVERY · SUBTOTAL. There is no Project column, no
 * Payment-type column, and no ⋮ actions menu on this screen in the design —
 * those three fields belong to a DIFFERENT, richer prototype binding
 * (`ordersTable`, ~L453) that only backs the Home "Recent orders" widget
 * (`RecentOrders.tsx`). An earlier UID-132 pass mistakenly imported that
 * Home layout onto this screen and flipped the ORDER-identity ordering to
 * match it (name primary) — both mistakes are reverted here; the identity
 * cell is back to SO-ref-primary / name-secondary. See `RecentOrders.tsx`
 * for the sibling (correctly richer) implementation and `domain.ts`'s
 * `resolveOrderIdentity` doc comment for the two screens' opposite
 * primary/secondary roles.
 */
export function OrderTable({
  orders,
  sortColumn,
  sortDirection,
  onSort,
  hasAnySearchOrFilterActive,
  onClearFilters,
}: OrderTableProps) {
  if (orders.length === 0) {
    return (
      <div className="data-table-wrapper">
        {hasAnySearchOrFilterActive ? (
          // §1.4 "Empty search/filter result" — distinct, lighter-weight
          // treatment from the true empty state; stat cards/spend summary
          // stay visible above this (the caller controls that).
          <EmptyState
            title="No orders match your search"
            action={
              <button type="button" className="btn-link" onClick={onClearFilters}>
                Clear filters
              </button>
            }
          />
        ) : (
          <EmptyState title="No orders yet" />
        )}
      </div>
    )
  }

  return (
    <div className="data-table-wrapper">
      <table className="data-table order-table">
        <caption className="sr-only">Your orders</caption>
        <thead>
          <tr>
            <th scope="col">Order</th>
            <th scope="col">Parts</th>
            <th scope="col">Status</th>
            <th scope="col">Est. delivery</th>
            <SortableHeader
              label="Subtotal"
              column="total"
              activeColumn={sortColumn}
              direction={sortDirection}
              onSort={onSort}
            />
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <OrderRow key={order.id} order={order} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function SortableHeader({
  label,
  column,
  activeColumn,
  direction,
  onSort,
}: {
  readonly label: string
  readonly column: OrderTableSortColumn
  readonly activeColumn: OrderTableSortColumn
  readonly direction: SortDirection
  readonly onSort: (column: OrderTableSortColumn) => void
}) {
  const isActive = activeColumn === column
  return (
    <th scope="col" aria-sort={isActive ? direction : 'none'}>
      <button type="button" className="ui-table-sort" onClick={() => onSort(column)}>
        {label}
        {isActive && (
          <span aria-hidden="true" className="sortable-header-arrow">
            {direction === 'ascending' ? '▲' : '▼'}
          </span>
        )}
      </button>
    </th>
  )
}

function OrderRow({ order }: { readonly order: OrderSummary }) {
  const meta = ORDER_STATUS_CHIP_META[order.status]
  const identity = resolveOrderIdentity(order)
  const partNames = resolvePartNamesDisplay(order)

  return (
    <tr className="order-row">
      <td>
        {/* Standalone /orders list (`ordersRows` prototype binding, ~L3629-
        3651): the SO ref is the PRIMARY line, with the human order name (when
        one exists) as the secondary caption underneath — the opposite of the
        Home "Recent orders" widget's identity ordering (`RecentOrders.tsx`,
        the different `ordersTable` binding). See domain.ts's
        `resolveOrderIdentity` doc comment. */}
        <Link to={`/orders/${order.id}`} className="order-row-identity-primary">
          {identity.ref}
        </Link>
        {identity.name && <span className="order-row-identity-subtitle">{identity.name}</span>}
      </td>
      <td className="order-row-parts">
        {/* `ordersRows` PARTS cell (`Fictiv Platform.dc.html` ~L3635-3643):
        a two-line stack — row 1 is the thumbnail swatches + "N parts · M
        units", row 2 is the part names in a bolder line below. */}
        <div className="order-row-parts-cell">
          <div className="order-row-parts-primary">
            <PartThumbnails count={order.lineCount} thumbnailUrls={order.thumbnailUrls} />
            <span className="order-row-parts-summary" title={formatPartsSummary(order)}>
              {formatPartsSummary(order)}
            </span>
          </div>
          {partNames && (
            <div className="order-row-parts-names">
              <span className="order-row-parts-name-primary" title={partNames.primary}>
                {partNames.primary}
              </span>
              {partNames.moreCount > 0 && (
                <span className="order-row-parts-more">+{partNames.moreCount} more</span>
              )}
            </div>
          )}
        </div>
      </td>
      <td>
        {/* UID-212 follow-up: the design's order status pill is squarish + colored, with NO icon
        (`Fictiv Platform.dc.html:3862` pill spec) — the color + label carry the meaning. */}
        <Chip tone={meta.tone}>{meta.label}</Chip>
      </td>
      <td className="order-est-delivery-cell">{formatEstDelivery(order)}</td>
      <td className="order-total-cell">
        {formatMoney(Number(order.total.amount), order.total.currency)}
      </td>
    </tr>
  )
}

export function OrderTableSkeleton({ rows = 6 }: { readonly rows?: number }) {
  return (
    <div className="data-table-wrapper" aria-hidden="true">
      <table className="data-table order-table">
        <thead>
          <tr>
            <th scope="col">Order</th>
            <th scope="col">Parts</th>
            <th scope="col">Status</th>
            <th scope="col">Est. delivery</th>
            <th scope="col">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, index) => index).map((index) => (
            <tr key={`skeleton-row-${index}`}>
              <td>
                <Skeleton width="70%" />
              </td>
              <td>
                <Skeleton width="60%" />
              </td>
              <td>
                <Skeleton width="80%" />
              </td>
              <td>
                <Skeleton width="60%" />
              </td>
              <td>
                <Skeleton width="50%" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
